import React, { useState, useEffect } from 'react';
import { formatDate, formatDateRange } from '../utils/dateUtils';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Typography,
  Box,
  Paper,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  InputAdornment,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Stack
} from '@mui/material';
import axios from 'axios';
import config from '../config';
import DurationSelector from './DurationSelector';
import NoteCard from './notes/NoteCard';
import { parseNotesBlob, createStructuredNote } from '../utils/noteUtils';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SchoolIcon from '@mui/icons-material/School';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import LinkIcon from '@mui/icons-material/Link';
import CancelIcon from '@mui/icons-material/Cancel';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';
import NotesIcon from '@mui/icons-material/Notes';
import Badge from '@mui/material/Badge';
import { updateSchoolOrder } from './SchoolOrderUpdate';
import OrderAttachments from './OrderAttachments';
import NotesDialog from './NotesDialog';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import InfoIcon from '@mui/icons-material/Info';
import WorkflowHistory from './orders/WorkflowHistory';
import EventIcon from '@mui/icons-material/Event';
import ImageIcon from '@mui/icons-material/Image';
import AttachFileIcon from '@mui/icons-material/AttachFile';

// Component to show update order changes
const UpdateOrderChanges = ({ order }) => {
  const [relatedOrder, setRelatedOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [organizationNames, setOrganizationNames] = useState({});
  const { getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    const fetchRelatedOrder = async () => {
      if (!order.relatedOrderId) {
        setLoading(false);
        return;
      }

      try {
        const token = await getAccessTokenSilently();
        const response = await axios.get(
          `${config.apiUrl}/api/orders/${order.relatedOrderId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const orderData = response.data.data || response.data;
        setRelatedOrder(orderData);

        // Fetch organization names for all audiences
        if (orderData.audiences && orderData.audiences.length > 0) {
          const orgIds = [...new Set(orderData.audiences.map(a => a.organization).filter(Boolean))];
          const orgNamesMap = {};
          
          for (const orgId of orgIds) {
            try {
              const orgResponse = await axios.get(
                `${config.apiUrl}/api/organizations/${orgId}`,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              if (orgResponse.data) {
                orgNamesMap[orgId] = orgResponse.data.name;
              }
            } catch (err) {
              console.error(`Error fetching organization ${orgId}:`, err);
              orgNamesMap[orgId] = 'Unknown';
            }
          }
          
          setOrganizationNames(orgNamesMap);
        }
      } catch (error) {
        console.error('Error fetching related order:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRelatedOrder();
  }, [order.relatedOrderId, getAccessTokenSilently]);

  // Parse comprehensive update details from notes
  const parseUpdateDetails = (notes) => {
    if (!notes) return { updates: [], additionalNotes: '' };
    
    const lines = notes.split('\n');
    const updates = [];
    let inUpdateSection = false;
    let additionalNotes = '';
    let beforeUpdateSection = '';

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      if (line.includes('Update Details:')) {
        inUpdateSection = true;
        additionalNotes = beforeUpdateSection.trim();
        continue;
      }
      
      if (inUpdateSection && line.trim()) {
        // Parse different update types
        if (line.includes('Date extension for group')) {
          const match = line.match(/Date extension for group (.*?): (.*?) ? (.*?) \(Cost: \$(.*?)\)/);
          if (match) {
            updates.push({
              type: 'dates',
              groupId: match[1],
              oldEndDate: match[2],
              newEndDate: match[3],
              cost: match[4]
            });
          }
        } else if (line.includes('Link update for group')) {
          const match = line.match(/Link update for group (.*?): (.*?) \(No cost\)/);
          if (match) {
            updates.push({
              type: 'link',
              groupId: match[1],
              linkType: match[2]
            });
          }
        } else if (line.includes('Artwork changes:')) {
          const match = line.match(/Artwork changes: (.*?) file\(s\) \(Cost: \$(.*?)\)/);
          if (match) {
            updates.push({
              type: 'artwork',
              changeCount: match[1],
              cost: match[2]
            });
          }
        }
      } else if (!inUpdateSection) {
        beforeUpdateSection += line + '\n';
      }
    }

    return { updates, additionalNotes };
  };

  const { updates, additionalNotes } = parseUpdateDetails(order.notes);

  // Get icon for update type
  const getUpdateIcon = (type) => {
    switch (type) {
      case 'dates':
        return <EventIcon sx={{ color: 'primary.main' }} />;
      case 'link':
        return <LinkIcon sx={{ color: 'info.main' }} />;
      case 'artwork':
        return <ImageIcon sx={{ color: 'warning.main' }} />;
      default:
        return <InfoIcon sx={{ color: 'text.secondary' }} />;
    }
  };

  // Get update type label
  const getUpdateTypeLabel = (type) => {
    switch (type) {
      case 'dates':
        return 'End Date Extension';
      case 'link':
        return 'Link Update';
      case 'artwork':
        return 'Artwork Change';
      default:
        return 'Update';
    }
  };

  return (
    <Box>
      {/* Update Order Header */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: 'info.50', borderLeft: 4, borderColor: 'info.main' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <InfoIcon sx={{ mr: 1, color: 'info.main' }} />
          <Typography variant="h6" color="info.main">
            Update Order Summary
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Related Order
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              #{order.relatedOrderId} - {order.relatedOrderBusinessName || 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Status
            </Typography>
            <Chip 
              label={order.status || 'New'} 
              color="primary" 
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Organisation
            </Typography>
            <Typography variant="body1">
              {order.organizationName || 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Created
            </Typography>
            <Typography variant="body1">
              {order.createdAt ? formatDate(order.createdAt) : 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Created By
            </Typography>
            <Typography variant="body1">
              {order.createdByName || 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Total Cost
            </Typography>
            <Typography variant="body1" color="success.main" fontWeight="bold" fontSize="1.1rem">
              ${parseFloat(order.totalPrice || 0).toFixed(2)}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Update Details - Show specific changes */}
      {updates.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CompareArrowsIcon sx={{ mr: 1, color: 'warning.main' }} />
            <Typography variant="h6">
              Update Details
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          
          {updates.map((update, index) => (
            <Paper 
              key={index} 
              variant="outlined" 
              sx={{ 
                p: 2, 
                mb: 2,
                borderLeft: 4,
                borderLeftColor: update.type === 'dates' ? 'primary.main' : 
                                 update.type === 'link' ? 'info.main' : 'warning.main'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                {getUpdateIcon(update.type)}
                <Typography variant="subtitle1" fontWeight="bold" sx={{ ml: 1 }}>
                  {getUpdateTypeLabel(update.type)}
                </Typography>
              </Box>
              
              {update.type === 'dates' && (
                <Box sx={{ pl: 4 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Audience Group:</strong> {update.groupId}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Previous End Date:</strong> {update.oldEndDate}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>New End Date:</strong> {update.newEndDate}
                  </Typography>
                  <Typography variant="body2" color="success.main" fontWeight="bold">
                    <strong>Extension Cost:</strong> ${update.cost}
                  </Typography>
                </Box>
              )}
              
              {update.type === 'link' && (
                <Box sx={{ pl: 4 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Audience Group:</strong> {update.groupId}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Link Type:</strong> {update.linkType}
                  </Typography>
                  {order.eLinkContent && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Link Content:</strong> {order.eLinkContent}
                    </Typography>
                  )}
                  <Typography variant="body2" color="info.main" fontWeight="bold">
                    <strong>Cost:</strong> No charge
                  </Typography>
                </Box>
              )}
              
              {update.type === 'artwork' && (
                <Box sx={{ pl: 4 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Number of Changes:</strong> {update.changeCount}
                  </Typography>
                  <Typography variant="body2" color="success.main" fontWeight="bold">
                    <strong>Artwork Cost:</strong> ${update.cost}
                  </Typography>
                </Box>
              )}
            </Paper>
          ))}
        </Paper>
      )}

      {/* Additional Notes with Client Revisions */}
      {(additionalNotes || order.notes) && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Order Notes
            </Typography>
            {(order.notes && order.notes.includes('CLIENT REVISION REQUEST')) && (
              <Button
                variant="outlined"
                color="warning"
                size="small"
                onClick={() => {
                  const revisionSection = document.getElementById('client-revisions-section');
                  if (revisionSection) {
                    revisionSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }
                }}
              >
                See Revisions
              </Button>
            )}
          </Box>
          <Divider sx={{ mb: 2 }} />
          <EmbeddedNotes order={order} orderType="update" />
        </Paper>
      )}

      {/* Update Order Attachments */}
      {order.id && (
        <Box sx={{ mb: 3 }}>
          <OrderAttachments 
            orderId={order.id}
            mode="full"
            title="Update Attachments"
          />
        </Box>
      )}

      {/* Related Order Information */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : relatedOrder ? (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Original Order Information
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Business Name
              </Typography>
              <Typography variant="body1">
                {relatedOrder.businessName || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Current Status
              </Typography>
              <Chip 
                label={relatedOrder.status || 'N/A'} 
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Total Price
              </Typography>
              <Typography variant="body1" color="success.main" fontWeight="bold">
                ${(() => {
                  // Try both totalPrice and total_price fields
                  const price = relatedOrder.totalPrice || relatedOrder.total_price || 0;
                  return typeof price === 'number' 
                    ? price.toFixed(2) 
                    : parseFloat(price || 0).toFixed(2);
                })()}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Product Type
              </Typography>
              <Typography variant="body1">
                {relatedOrder.productType || 'N/A'}
              </Typography>
            </Grid>
            {relatedOrder.audiences && relatedOrder.audiences.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Groups ({relatedOrder.audiences.length})
                </Typography>
                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                  {relatedOrder.audiences.map((audience, index) => {
                    const orgName = organizationNames[audience.organization] || 'Unknown School';
                    return (
                      <Box 
                        key={index}
                        sx={{ 
                          mb: 1.5,
                          p: 1.5,
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 1,
                          bgcolor: 'background.paper'
                        }}
                      >
                        <Typography variant="body2" fontWeight="bold" gutterBottom>
                          Group {index + 1} - {audience.channel || 'N/A'} - {audience.adType || 'N/A'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>School:</strong> {orgName}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Grid>
            )}
          </Grid>
        </Paper>
      ) : (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography color="text.secondary">
            Original order information not available
          </Typography>
        </Paper>
      )}

      {/* Original Order Attachments */}
      {relatedOrder?.id && (
        <Box sx={{ mb: 3 }}>
          <OrderAttachments 
            orderId={relatedOrder.id}
            mode="full"
            title="Original Order Attachments"
          />
        </Box>
      )}
    </Box>
  );
};

// Helper function to parse client revision requests from design notes
const parseClientRevisions = (designNotes) => {
  if (!designNotes) return { cleanDesignNotes: '', clientRevisions: [] };
  
  const revisionSeparator = '?? === CLIENT REVISION REQUEST ===';
  const parts = designNotes.split(revisionSeparator);
  
  // First part is the original design notes (before any revisions)
  const cleanDesignNotes = parts[0].trim();
  
  // Parse each revision request
  const clientRevisions = [];
  for (let i = 1; i < parts.length; i++) {
    const revisionText = parts[i];
    
    // Extract date and feedback using regex
    const dateMatch = revisionText.match(/Date: ([^\n]+)/);
    const feedbackMatch = revisionText.match(/Client Feedback: ([^\n]+)/);
    
    if (dateMatch && feedbackMatch) {
      const dateStr = dateMatch[1];
      const feedback = feedbackMatch[1];
      
      // Format the date to match the desired format (dd/mm/yy, hh:mm am/pm)
      try {
        const date = new Date(dateStr.replace(/(\d{2})\/(\d{2})\/(\d{4})/, '$3-$2-$1'));
        const formattedDate = date.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: '2-digit'
        }) + ', ' + date.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        }).toLowerCase();
        
        clientRevisions.push({
          feedback,
          date: formattedDate,
          originalDate: dateStr
        });
      } catch (error) {
        // If date parsing fails, use original format
        clientRevisions.push({
          feedback,
          date: dateStr,
          originalDate: dateStr
        });
      }
    }
  }
  
  return { cleanDesignNotes, clientRevisions };
};

// EmbeddedNotes Component - Display notes directly in order details.
// Every note - regardless of whether it originated from an offer, an enquiry,
// order creation, or the notes dialog - is parsed into an individually
// attributed entry and rendered with the same NoteCard, instead of dumping
// the raw (potentially multi-note) text blob as one paragraph.
const EmbeddedNotes = ({ order, orderType = 'order' }) => {
  const sections = [];

  const pushSection = (title, content, options) => {
    const notes = parseNotesBlob(content, {
      idPrefix: options.idPrefix,
      createdAt: order.createdAt,
      noteType: options.noteType
    });
    if (notes.length > 0) {
      sections.push({ title, notes, ...options });
    }
  };

  // For update orders, notes field contains everything (including revisions)
  // For ad orders, designNotes contains revisions
  const notesFieldToCheck = orderType === 'update' ? order.notes : order.designNotes;

  // Parse client revisions from the appropriate field
  const { cleanDesignNotes, clientRevisions } = parseClientRevisions(notesFieldToCheck);

  // For update orders, display the clean notes (without revision markers)
  // For ad orders, display sales notes separately
  if (orderType === 'update') {
    pushSection('Order Notes', cleanDesignNotes, {
      idPrefix: 'order-notes', noteType: 'general', color: 'primary', bgColor: 'blue.50', borderColor: 'blue.200'
    });
  } else {
    pushSection('Sales Notes', order.notes, {
      idPrefix: 'sales-notes', noteType: 'general', color: 'primary', bgColor: 'blue.50', borderColor: 'blue.200'
    });
    pushSection('Design Notes', cleanDesignNotes, {
      idPrefix: 'design-notes', noteType: 'design', color: 'secondary', bgColor: 'purple.50', borderColor: 'purple.200'
    });
  }

  // Add client notes section if there are any client revisions
  if (clientRevisions.length > 0) {
    sections.push({
      title: 'Client Revision Requests',
      color: 'warning',
      bgColor: 'orange.50',
      borderColor: 'orange.200',
      id: 'client-revisions-section',
      notes: clientRevisions.map((revision, idx) => ({
        id: `client-revision-${idx}`,
        content: revision.feedback,
        createdBy: revision.createdBy || 'Client',
        createdAt: revision.createdAt,
        sourceLabel: null,
        isNew: false
      }))
    });
  }

  pushSection('Customer Notes', order.customerNotes, {
    idPrefix: 'customer-notes', noteType: 'customer', color: 'success', bgColor: 'green.50', borderColor: 'green.200'
  });

  if (sections.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 3, px: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No notes available for this order.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {sections.map((section, index) => (
        <Box
          key={section.title}
          id={section.id}
          sx={{ mb: index < sections.length - 1 ? 3 : 0 }}
        >
          <Typography
            variant="subtitle2"
            color={section.color}
            fontWeight="bold"
            gutterBottom
            display="block"
          >
            {section.title} ({section.notes.length}):
          </Typography>
          <Stack spacing={1.5}>
            {section.notes.map((note) => (
              <NoteCard
                key={note.id}
                note={note}
                color={section.color}
                bgColor={section.bgColor}
                borderColor={section.borderColor}
              />
            ))}
          </Stack>
        </Box>
      ))}
    </Box>
  );
};

// ProofButtons Component - Reusable for different contexts
const ProofButtons = ({ orderId, onProofCancelled, variant = 'section' }) => {
  const [proofInfo, setProofInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProofInfo();
  }, [orderId]);

  const fetchProofInfo = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${config.apiUrl}/api/proof/order/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setProofInfo(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching proof info:', error);
      setError('Failed to load proof information');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelProof = async () => {
    if (!window.confirm('Are you sure you want to cancel this proof? This will reset the order status to "In Design" and make the proof link inactive.')) {
      return;
    }

    try {
      setCancelling(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${config.apiUrl}/api/proof/cancel/${orderId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setProofInfo(null);
        if (onProofCancelled) {
          onProofCancelled();
        }
      }
    } catch (error) {
      console.error('Error cancelling proof:', error);
      setError('Failed to cancel proof');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return variant === 'buttons' ? null : (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
        <CircularProgress size={20} />
        <Typography variant="body2">Loading proof information...</Typography>
      </Box>
    );
  }

  if (error && variant === 'section') {
    return (
      <Box sx={{ p: 2, bgcolor: 'error.light', borderRadius: 1, border: 1, borderColor: 'error.main' }}>
        <Typography variant="body2" color="error.main">{error}</Typography>
      </Box>
    );
  }

  if (!proofInfo) {
    return variant === 'buttons' ? null : (
      <Box sx={{ p: 2, bgcolor: 'warning.light', borderRadius: 1, border: 1, borderColor: 'warning.main' }}>
        <Typography variant="body2" color="warning.dark">
          Order status is "Proof Pending" but no active proof found.
        </Typography>
      </Box>
    );
  }

  // Button variant for inline use (like workflow table)
  if (variant === 'buttons') {
    return (
      <>
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<OpenInNewIcon />}
          onClick={() => window.open(proofInfo.proofLink, '_blank')}
        >
          View Proof
        </Button>
        <Button
          variant="outlined"
          color="error"
          size="small"
          startIcon={<CancelIcon />}
          onClick={handleCancelProof}
          disabled={cancelling}
        >
          {cancelling ? 'Cancelling...' : 'Cancel Proof'}
        </Button>
      </>
    );
  }

  // Section variant for detailed display (order details)
  return (
    <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 1, border: 1, borderColor: 'info.main' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LinkIcon color="info" />
          <Typography variant="h6" color="info.dark">
            Proof Available
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
                            Created: {formatDate(proofInfo.createdAt)}
        </Typography>
      </Box>
      
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<OpenInNewIcon />}
          onClick={() => window.open(proofInfo.proofLink, '_blank')}
          sx={{ minWidth: 140 }}
        >
          View Proof
        </Button>
        
        <Button
          variant="outlined"
          color="error"
          startIcon={<CancelIcon />}
          onClick={handleCancelProof}
          disabled={cancelling}
          sx={{ minWidth: 120 }}
        >
          {cancelling ? 'Cancelling...' : 'Cancel Proof'}
        </Button>
      </Box>
      
      <Typography variant="body2" color="text.secondary">
        Proof URL: {proofInfo.proofLink}
      </Typography>
    </Box>
  );
};

// ProofSection Component - For detailed order view
const ProofSection = ({ orderId, onProofCancelled }) => {
  return <ProofButtons orderId={orderId} onProofCancelled={onProofCancelled} variant="section" />;
};

// PDF Download Component - For downloading Terms and Conditions PDFs
const PDFDownloadButton = ({ orderId, variant = 'section' }) => {
  const [pdfStatus, setPdfStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkPDFStatus();
  }, [orderId]);

  const checkPDFStatus = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${config.apiUrl}/api/proof/pdf-status/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setPdfStatus(response.data);
      }
    } catch (error) {
      console.error('Error checking PDF status:', error);
      setError('Failed to check PDF status');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      setDownloading(true);
      const token = localStorage.getItem('token');
      
      // Create a download link
      const response = await fetch(`${config.apiUrl}/api/proof/download/invoice/${orderId}`, {
        method: 'GET',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/pdf'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download PDF');
      }

      // Get the filename from the response headers or create a default one
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `TERMS-${orderId}-Order.pdf`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

    } catch (error) {
      console.error('Error downloading PDF:', error);
      setError('Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return variant === 'button' ? null : (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="body2">Checking PDF availability...</Typography>
      </Box>
    );
  }

  if (error && variant === 'section') {
    return (
      <Box sx={{ p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
        <Typography variant="body2" color="error.main">{error}</Typography>
      </Box>
    );
  }

  if (!pdfStatus?.hasPDF) {
    return variant === 'button' ? null : (
      <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
        <Typography variant="body2" color="info.dark">
          No Terms and Conditions PDF available for this order.
        </Typography>
      </Box>
    );
  }

  // Button variant for inline use
  if (variant === 'button') {
    return (
      <Button
        variant="contained"
        color="secondary"
        size="small"
        startIcon={<DownloadIcon />}
        onClick={handleDownloadPDF}
        disabled={downloading}
      >
        {downloading ? 'Downloading...' : 'Download Terms PDF'}
      </Button>
    );
  }

  // Section variant for detailed view
  return (
    <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 1, border: 1, borderColor: 'success.main' }}>
      <Typography variant="subtitle2" color="success.dark" gutterBottom>
        ?? Terms & Conditions PDF Available
      </Typography>
      
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadPDF}
          disabled={downloading}
        >
          {downloading ? 'Downloading...' : 'Download Terms PDF'}
        </Button>
      </Box>
      
      <Typography variant="body2" color="text.secondary">
        Invoice: {pdfStatus.invoiceNumber} � Generated: {new Date(pdfStatus.pdfGeneratedAt).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: '2-digit'
        })}
      </Typography>
    </Box>
  );
};

const OrderDetail = ({ open, onClose, order, onUpdate }) => {
  const { getAccessTokenSilently, user } = useAuth0();
  
  // Normalize status values coming from database
  const normalizeStatus = (status) => {
    if (!status) return 'New';
    
    const statusStr = String(status).toLowerCase().trim();
    
    // Handle numeric status values
    if (statusStr === '1') return 'Prospect';
    if (statusStr === '2') return 'New';
    if (statusStr === '3') return 'Waiting for Artwork';
    if (statusStr === '4') return 'In Design';
    if (statusStr === '5') return 'Proofing';
    if (statusStr === '6') return 'Proof Pending';
    if (statusStr === '7') return 'Approved';
    if (statusStr === '8') return 'Uploaded';
    if (statusStr === '9') return 'Invoiced';
    if (statusStr === '10') return 'Cancelled';
    
    // Handle string status values - capitalize first letter of each word
    const statusMap = {
      'prospect': 'Prospect',
      'new': 'New',
      'quoting': 'Quoting',
      'waiting for artwork': 'Waiting for Artwork',
      'in design': 'In Design',
      'proofing': 'Proofing',
      'proof pending': 'Proof Pending',
      'proof_pending': 'Proof Pending',
      'approved': 'Approved',
      'in production': 'In Production',
      'in_production': 'In Production',
      'uploaded': 'Uploaded',
      'invoiced': 'Invoiced',
      'cancelled': 'Cancelled'
    };
    
    return statusMap[statusStr] || status
      .split(/[_\s]/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const [editedOrder, setEditedOrder] = useState({
    ...(order || {}),
    id: order?.id,
    // Ensure required fields have fallbacks to prevent null reference errors
    businessNumber: order?.businessNumber || order?.business_number || '',
    businessName: order?.businessName || order?.business_name || '',
    businessType: order?.businessType || order?.business_type || '',
    // Add organization fields - support both camelCase and snake_case
    organization: order?.organization || order?.organization_id || null,
    organizationId: order?.organizationId || order?.organization_id || null,
    // Product type for ad orders
    productType: order?.productType || order?.product_type || '',
    // Only include status if provided in the order, normalize it
    status: normalizeStatus(order?.status),
    // Include active status field
    active: order?.active !== undefined ? order?.active : true,
    notes: order?.notes || '',
    designNotes: order?.designNotes || order?.design_notes || '',
    // Invoice settings for ad orders
    invoiceLater: order?.invoiceLater || false,
    invoiceDate: order?.invoiceDate || null,
    paid: order?.paid || false,
    // Sales person information
    salesPersonId: order?.salesPersonId || order?.sales_person_id || null,
    salesPersonName: order?.salesPersonName || null,
    createdById: order?.createdById || order?.created_by_id || null,
    createdByName: order?.createdByName || null,
    audiences: Array.isArray(order?.audiences) 
      ? order.audiences 
      : (typeof order?.audiences === 'string' 
          ? JSON.parse(order.audiences) 
          : [])
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [organizations, setOrganizations] = useState([]);
  const [salesPeople, setSalesPeople] = useState([]);
  const [schoolData, setSchoolData] = useState(null);
  const [orderType, setOrderType] = useState('');
  const [isSchoolOrder, setIsSchoolOrder] = useState(false);
  const [isAdOrder, setIsAdOrder] = useState(false);
  const [audiences, setAudiences] = useState([]);
  
  // Notes dialog state
  const [notesDialog, setNotesDialog] = useState({
    open: false,
    title: '',
    entityType: '',
    entityId: '',
    notes: []
  });

  // Embedded notes state
  const [newNote, setNewNote] = useState('');

  useEffect(() => {
    if (open && order) {
      // Order detail opened - fetch fresh data from backend
      
      
      const fetchFreshOrderData = async () => {
        try {
          const token = localStorage.getItem('token');
          let endpoint;
          
          // Determine the correct endpoint based on order type
          if (order.orderType === 'ad' || order.order_type === 'ad') {
            endpoint = `${config.apiUrl}/api/ad-orders/${order.id}`;
          } else if (order.orderType === 'school' || order.order_type === 'school') {
            endpoint = `${config.apiUrl}/api/school-orders/${order.id}`;
          } else if (order.orderType === 'stationery' || order.order_type === 'stationery') {
            endpoint = `${config.apiUrl}/api/stationery-orders/${order.id}`;
          } else {
            endpoint = `${config.apiUrl}/api/orders/${order.id}`;
          }
          

          
          const response = await axios.get(endpoint, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          let freshOrderData;
          if (response.data && response.data.success) {
            freshOrderData = response.data.data;
            
          } else {
            console.warn('?? Unexpected response format, using passed order data');
            freshOrderData = order;
          }
          
          // Use the fresh data instead of the passed order data
          const mappedOrder = mapOrderData(freshOrderData);
          setEditedOrder(mappedOrder);
          
          // Set local state variables from the mapped order
          if (mappedOrder.orderType === 'school') {
            setIsSchoolOrder(true);
          } else if (mappedOrder.orderType === 'ad') {
            setIsAdOrder(true);
          }
          
          // Set audiences from the fresh order if available
          if (freshOrderData.audiences && Array.isArray(freshOrderData.audiences)) {
            setAudiences(freshOrderData.audiences);
          }
          
        } catch (error) {
          console.error('? Error fetching fresh order data:', error);
          // Fall back to using the passed order data
          const mappedOrder = mapOrderData(order);
          setEditedOrder(mappedOrder);
          
          if (mappedOrder.orderType === 'school') {
            setIsSchoolOrder(true);
          } else if (mappedOrder.orderType === 'ad') {
            setIsAdOrder(true);
          }
          
          if (order.audiences && Array.isArray(order.audiences)) {
            setAudiences(order.audiences);
          }
        }
      };
      
      // Fetch sales people for dropdown
      const fetchSalesPeople = async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${config.apiUrl}/api/users`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          // Ensure the data is always an array
          if (response.data && response.data.data && Array.isArray(response.data.data)) {
            // If response contains data property with an array (new API format)
            setSalesPeople(response.data.data);
          } else if (Array.isArray(response.data)) {
            // If response is directly an array (old API format)
            setSalesPeople(response.data);
          } else {
            // Fallback to empty array to prevent errors
            console.warn('Unexpected sales people data format:', response.data);
            setSalesPeople([]);
          }
        } catch (error) {
          console.error('Error fetching sales people:', error);
          // Set empty array on error to prevent map() issues
          setSalesPeople([]);
        }
      };
      
      // Execute both fetch operations
      fetchFreshOrderData();
      fetchSalesPeople();
    }
  }, [open, order]);

  useEffect(() => {
    if (order) {
      // Processing order data
      
      // Check if audiences exists and handle it properly
      const parsedOrder = {
        ...order,
        id: order.id || order.orderId, // Support both id and orderId formats
        
        // Ensure required fields have fallbacks with both camelCase and snake_case support
        businessNumber: order?.businessNumber || order?.business_number || '',
        businessName: order?.businessName || order?.business_name || order?.schoolName || '',
        businessType: order?.businessType || order?.business_type || '',
        
        // Add organization fields with both camelCase and snake_case support
        organization: order?.organization || order?.organization_id || null,
        organizationId: order?.organizationId || order?.organization_id || null,
        
        // Product type for ad orders
        productType: order?.productType || order?.product_type || '',
        
        // Support both formats for notes and design notes
        notes: order?.notes || '',
        designNotes: order?.designNotes || order?.design_notes || '',
        
        // Keep original status without default
        status: order?.status,
        
        // Keep endDate from order if available
        endDate: order?.endDate || null,
        
        // Sales person info with both camelCase and snake_case support
        salesPersonId: order?.salesPersonId || order?.sales_person_id || null,
        salesPersonName: order?.salesPersonName || null,
        
        // Created by info with both camelCase and snake_case support
        createdById: order?.createdById || order?.created_by_id || null,
        createdByName: order?.createdByName || null,
        
        // Parse audiences properly
        audiences: Array.isArray(order?.audiences) 
          ? order.audiences 
          : (typeof order?.audiences === 'string' 
              ? JSON.parse(order.audiences) 
              : [])
      };
      setEditedOrder(parsedOrder);
      
      // If organizationData is provided, ensure it's used
      if (order.organizationData) {
        // Update businessName to use organization name if not explicitly set
        if (!order.businessName && !order.business_name) {
          parsedOrder.businessName = order.organizationData.name;
        }
      }
      
      // Store sales person information
      if (order.salesPersonId || order.sales_person_id || order.salesPersonName) {
        parsedOrder.salesPersonId = order.salesPersonId || order.sales_person_id;
        parsedOrder.salesPersonName = order.salesPersonName;
      }
      
      // Always store creator information directly from API if available
      if (order.createdById || order.created_by_id || order.createdByName) {
        parsedOrder.createdById = order.createdById || order.created_by_id;
        parsedOrder.createdByName = order.createdByName;
      } else if (order.salesPersonId || order.sales_person_id) {
        // Only fall back to sales person as creator if no creator info at all
        parsedOrder.createdById = order.salesPersonId || order.sales_person_id;
        parsedOrder.salesPersonName = order.salesPersonName;
      }
      
      // Check if this is a school order by using the explicit order type
      if (order.orderType === 'school' || order.order_type === 'school') {
        setOrderType('school');
        
        // For school orders, we don't need sales person information
        delete parsedOrder.salesPersonId;
        delete parsedOrder.salesPersonName;
        
        // Parse school order data if this is a school order
        // First try to get data from dedicated fields
        let schoolOrderData = {
          schoolInfo: {
            name: order.schoolName || order.businessName || ''
          },
          submittedBy: {
            firstName: order.firstName || '',
            lastName: order.lastName || '',
            position: order.position || '',
            email: order.email || '',
            phone: order.phone || ''
          },
          newsletter: {
            numberOfFamilies: order.numberOfFamilies || '',
            issuePerTerm: order.issuesPerTerm || ''
          },
          paymentModel: order.paymentModel || 'adSupported',
          // Include endDate from order
          endDate: order.endDate || null,
          productsOrdered: order.productsOrdered || []
        };
        
        // Then try to parse from notes if those fields are missing
        if (order?.notes) {
          try {
            let parsedNotes;
            
            try {
              parsedNotes = JSON.parse(order.notes);
            } catch (e) {
              console.error('Failed to parse notes as JSON:', e);
              // Try to use it as-is if it's not JSON
              parsedNotes = order.notes;
            }
            
            // Check for our new fullContactInfo format
            if (parsedNotes.fullContactInfo) {
              schoolOrderData.submittedBy.firstName = parsedNotes.firstName || schoolOrderData.submittedBy.firstName;
              schoolOrderData.submittedBy.lastName = parsedNotes.lastName || schoolOrderData.submittedBy.lastName;
              schoolOrderData.submittedBy.position = parsedNotes.position || schoolOrderData.submittedBy.position;
              schoolOrderData.submittedBy.email = parsedNotes.email || schoolOrderData.submittedBy.email;
              schoolOrderData.submittedBy.phone = parsedNotes.phone || schoolOrderData.submittedBy.phone;
            } 
            // Support legacy format with submittedBy nested structure
            else if (parsedNotes.submittedBy) {
              if (parsedNotes.schoolInfo && !order.schoolName) {
                schoolOrderData.schoolInfo = parsedNotes.schoolInfo;
              }
              
              if (!order.firstName && parsedNotes.submittedBy.firstName) {
                schoolOrderData.submittedBy.firstName = parsedNotes.submittedBy.firstName;
              } else if (!order.firstName && parsedNotes.submittedBy.name) {
                // For backward compatibility with older records
                const nameParts = parsedNotes.submittedBy.name.split(' ');
                schoolOrderData.submittedBy.firstName = nameParts[0] || '';
                schoolOrderData.submittedBy.lastName = nameParts.slice(1).join(' ') || '';
              }
              
              if (!order.lastName && parsedNotes.submittedBy.lastName) {
                schoolOrderData.submittedBy.lastName = parsedNotes.submittedBy.lastName;
              }
              
              if (!order.position && parsedNotes.submittedBy.position) {
                schoolOrderData.submittedBy.position = parsedNotes.submittedBy.position;
              }
              
              if (!order.email && parsedNotes.submittedBy.email) {
                schoolOrderData.submittedBy.email = parsedNotes.submittedBy.email;
              }
              
              if (!order.phone && parsedNotes.submittedBy.phone) {
                schoolOrderData.submittedBy.phone = parsedNotes.submittedBy.phone;
              }
              
              if (parsedNotes.newsletter) {
                if (!order.numberOfFamilies && parsedNotes.newsletter.numberOfFamilies) {
                  schoolOrderData.newsletter.numberOfFamilies = parsedNotes.newsletter.numberOfFamilies;
                }
                if (!order.issuesPerTerm && parsedNotes.newsletter.issuePerTerm) {
                  schoolOrderData.newsletter.issuePerTerm = parsedNotes.newsletter.issuePerTerm;
                }
              }
              
              if (!order.paymentModel && parsedNotes.paymentModel) {
                schoolOrderData.paymentModel = parsedNotes.paymentModel;
              }
              
              if (!order.productsOrdered && parsedNotes.productsOrdered) {
                schoolOrderData.productsOrdered = parsedNotes.productsOrdered;
              }
            }
          } catch (e) {
            console.error('Error parsing school order data from notes:', e);
          }
        }
        
        // Process productsOrdered if it's a string
        if (typeof schoolOrderData.productsOrdered === 'string' && schoolOrderData.productsOrdered) {
          schoolOrderData.productsOrdered = schoolOrderData.productsOrdered.split(',').map(item => item.trim());
        }
        
        setSchoolData(schoolOrderData);
      } else if (order.orderType === 'stationery' || order.order_type === 'stationery') {
        setOrderType('stationery');
        setSchoolData(null);
      } else if (order.orderType === 'ad' || order.order_type === 'ad') {
        setOrderType('advertising');
        setSchoolData(null);
      } else {
        // If orderType is not explicitly set, try to guess based on available data
        if (order.productType || order.product_type) {
          setOrderType('advertising');
          setSchoolData(null);
        } else if (order.schoolName || (order.notes && order.notes.includes('schoolInfo'))) {
          setOrderType('school');
        } else {
          setOrderType('advertising');
          setSchoolData(null);
        }
      }
    }
    
    // Fetch organizations for the dropdown
    const fetchOrganizations = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${config.apiUrl}/api/organizations?limit=1000`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        // Organisations fetched successfully
        // Handle both old format (array) and new format (object with data property)
        const orgsData = response.data;
        if (orgsData && orgsData.success && orgsData.data) {
          setOrganizations(orgsData.data);
        } else if (Array.isArray(orgsData)) {
          setOrganizations(orgsData);
        } else {
          setOrganizations([]);
        }
      } catch (error) {
        console.error('Error fetching organizations:', error);
        setOrganizations([]);
      }
    };
    
    fetchOrganizations();
  }, [order]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    
    // Only update the specific field without creating snake_case versions
    setEditedOrder({
      ...editedOrder,
      [field]: value
    });
  };

  // Function to get available ad types based on selected channel
  const getAvailableAdTypes = (channel) => {
    if (!channel) return [];
    
    switch (channel) {
      case 'Paper':
        return ['Standard', 'Budget', 'Premium', 'POW'];
      case 'ePublisher':
        return ['Standard', 'Budget', 'Premium', 'POW'];
      case 'SchoolLink':
        return ['Single Image', 'Carousel', 'Video or Animation', 'Standard', 'Premium'];
      default:
        return [];
    }
  };

  // Calculate subtotal for an audience with $ or % discount support
  const calculateSubTotal = (audience) => {
    const price = parseFloat(audience.price || 0);
    const discount = parseFloat(audience.discount || 0);
    const discountType = audience.discountType || '$';
    
    if (discount === 0) {
      return price.toFixed(2);
    }
    
    let subtotal;
    if (discountType === '%') {
      // Percentage discount: subtract percentage of price
      const discountAmount = (price * discount) / 100;
      subtotal = Math.max(0, price - discountAmount);
    } else {
      // Dollar discount: subtract fixed amount
      subtotal = Math.max(0, price - discount);
    }
    
    return subtotal.toFixed(2);
  };

  // Calculate total for all audiences
  const calculateTotal = () => {
    if (!editedOrder.audiences || editedOrder.audiences.length === 0) return 0;
    
    return editedOrder.audiences.reduce((total, audience) => {
      return total + parseFloat(calculateSubTotal(audience) || 0);
    }, 0);
  };

  // Function to get available channels for an organization
  const getAvailableChannels = (organizationId) => {
    if (!organizationId) return [];
    
    const selectedOrg = organizations.find(org => org.id === organizationId);
    if (!selectedOrg) return [];
    
    const channels = [];
    
    // Add channels based on organization properties
    if (selectedOrg.hasSchoolLink === true || selectedOrg.hasSchoolLink === 1) {
      channels.push("SchoolLink");
    }
    
    if (selectedOrg.hasEPublisher === true || selectedOrg.hasEPublisher === 1) {
      channels.push("ePublisher");
    }
    
    if (selectedOrg.hasPaper === true || selectedOrg.hasPaper === 1) {
      channels.push("Paper");
    }
    
    // If this is a school and no channels are available, provide default channels
    if (channels.length === 0 && (selectedOrg.isSchool === true || selectedOrg.isSchool === 1)) {
      // Default channels for schools - can be customized based on business requirements
      channels.push("SchoolLink", "ePublisher", "Paper");
    }
    
    return channels;
  };

  // Filter organizations to only show Active schools with Ad Supported payment model
  const adSupportedSchoolOrganizations = Array.isArray(organizations) 
    ? organizations.filter(org => 
        (org.isSchool === true || org.isSchool === 1) && 
        (org.schoolStatus === 'Active' || !org.schoolStatus) &&
        (org.paymentModel === 'Ad Supported' || org.paymentModel !== 'Paid')
      )
    : [];

  // Function to get organization price
  const getOrganizationPrice = (organizationId) => {
    if (!Array.isArray(organizations)) return null;
    const org = organizations.find(org => org.id === organizationId);
    if (org && org.price) {
      return parseFloat(org.price);
    }
    return null;
  };

  // Function to calculate price using server-side API with weekly rates
  const calculatePriceWithDates = async (basePrice, channel, adType, startDate, endDate) => {
    if (!basePrice || !channel || !adType || !startDate || !endDate) {
      return basePrice;
    }
    
    try {
      // Get the auth token
      const authToken = await getAccessTokenSilently({
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        scope: 'read:current_user'
      });
      
      if (!authToken) {
        console.warn('No auth token available, using base price');
        return basePrice;
      }
      
      // Make direct API call with proper token
      const response = await fetch(`${config.apiUrl}/api/pricing/calculate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          basePrice,
          channel,
          adType,
          startDate,
          endDate
        })
      });
      
      const result = await response.json();
      
      if (response.ok && result.success) {
        return result.price;
      } else {
        console.warn('Price calculation failed, using base price:', result.message);
        return basePrice;
      }
    } catch (error) {
      console.error('Price calculation error:', error);
      return basePrice; // Fallback to base price
    }
  };

  const handleAudienceChange = (index, field, value) => {
    const newAudiences = [...editedOrder.audiences];
    
    // Special case for organization field to check for duplicates
    if (field === 'organization') {
      // Check if this organization is already in any other audience
      const isDuplicate = editedOrder.audiences.some(
        (audience, i) => i !== index && audience.organization === value
      );
      
      // If it's a duplicate, show an error message and don't update
      if (isDuplicate) {
        setSnackbar({
          open: true,
          message: 'This organization is already added as an audience. Please use a different organization.',
          severity: 'error'
        });
        return;
      }
      
      // Create a new audience object with the organization set
      const updatedAudience = {
        ...newAudiences[index],
        organization: value === '' ? null : parseInt(value)
      };
      
      // Also check if we should set the price in the same update
      const basePrice = getOrganizationPrice(value);
      if (basePrice) {
        // Calculate price based on current channel, ad type, and dates
        const channel = updatedAudience.channel;
        const adType = updatedAudience.adType;
        const startDate = updatedAudience.startDate;
        const endDate = updatedAudience.endDate;
        
        if (channel && adType && startDate && endDate) {
          // Use async pricing calculation with dates
          calculatePriceWithDates(basePrice, channel, adType, startDate, endDate)
            .then(calculatedPrice => {
              const currentAudiences = [...editedOrder.audiences];
              const targetIndex = currentAudiences.findIndex(a => a === newAudiences[index]);
              if (targetIndex !== -1) {
                currentAudiences[targetIndex].price = calculatedPrice;
                setEditedOrder({
                  ...editedOrder,
                  audiences: currentAudiences
                });
              }
            });
        } else {
          // Fallback to base price if dates are missing
          updatedAudience.price = basePrice;
        }
      }
      
      // Update the audience in the array
      newAudiences[index] = updatedAudience;
    }
    // Price and discount need to be numbers
    else if (field === 'price' || field === 'discount') {
      newAudiences[index][field] = value === '' ? '' : parseFloat(value);
    }
    // If it's a date change, recalculate price if we have all required data
    else if (field === 'startDate' || field === 'endDate') {
      newAudiences[index][field] = value;
      
      // Recalculate price if we have all the data needed
      const audience = newAudiences[index];
      if (audience.organization && audience.channel && audience.adType && audience.startDate && audience.endDate) {
        const basePrice = getOrganizationPrice(audience.organization);
        if (basePrice) {
          calculatePriceWithDates(basePrice, audience.channel, audience.adType, audience.startDate, audience.endDate)
            .then(calculatedPrice => {
              const currentAudiences = [...editedOrder.audiences];
              currentAudiences[index] = {
                ...currentAudiences[index],
                [field]: value,
                price: calculatedPrice
              };
              setEditedOrder({
                ...editedOrder,
                audiences: currentAudiences
              });
            });
        }
      }
    }
    // If it's channel or adType, handle price recalculation
    else if (field === 'channel' || field === 'adType') {
      // Create a copy of the current audience
      const updatedAudience = { ...newAudiences[index] };
      
      // Update the specified field
      updatedAudience[field] = value;
      
      // When channel changes, we need to clear adType if it's not valid for the new channel
      if (field === 'channel') {
        // Get available ad types for new channel
        const availableAdTypes = getAvailableAdTypes(value);
        // Check if current adType is valid for the new channel
        if (updatedAudience.adType && !availableAdTypes.includes(updatedAudience.adType)) {
          // Clear adType if it's not valid
          updatedAudience.adType = '';
        }
      }
      
      // Try to recalculate price if we have organization, channel, adType and dates
      const organizationId = updatedAudience.organization;
      if (organizationId && updatedAudience.channel && updatedAudience.adType) {
        const basePrice = getOrganizationPrice(organizationId);
        if (basePrice) {
          const startDate = updatedAudience.startDate;
          const endDate = updatedAudience.endDate;
          
          if (startDate && endDate) {
            // Use async pricing calculation with dates
            calculatePriceWithDates(basePrice, updatedAudience.channel, updatedAudience.adType, startDate, endDate)
              .then(calculatedPrice => {
                const currentAudiences = [...editedOrder.audiences];
                const targetIndex = currentAudiences.findIndex(a => a.organization === organizationId);
                if (targetIndex !== -1) {
                  currentAudiences[targetIndex] = {
                    ...currentAudiences[targetIndex],
                    [field]: value,
                    price: calculatedPrice
                  };
                  setEditedOrder({
                    ...editedOrder,
                    audiences: currentAudiences
                  });
                }
              });
          } else {
            // Fallback to base price if dates are missing
            updatedAudience.price = basePrice;
          }
        }
      }
      
      // Update the audience in the array
      newAudiences[index] = updatedAudience;
    }
    // Otherwise, just use the value as is
    else {
      newAudiences[index][field] = value;
    }
    
    // Log the updated audience for debugging
    
    
    // Apply a single state update
    setEditedOrder({
      ...editedOrder,
      audiences: newAudiences
    });
  };

  const addAudience = () => {
    // Get the organization ID we want to add
    const organizationId = editedOrder.organization || null;
    
    // Check if this organization is already in the audiences array
    const isDuplicate = editedOrder.audiences.some(
      audience => audience.organization === organizationId
    );
    
    // If it's a duplicate, show an error message
    if (isDuplicate) {
      setSnackbar({
        open: true,
        message: 'This organization is already added as an audience. Please use a different organization.',
        severity: 'error'
      });
      return;
    }
    
    // If it's not a duplicate, add the new audience
    setEditedOrder({
      ...editedOrder,
      audiences: [
        ...editedOrder.audiences,
        {
          // Use the order's organization as default for new audiences
          organization: organizationId,
          channel: '',
          adType: '',
          startDate: '',
          endDate: '',
          price: '',
          discount: '0',
          eLinkType: 'None',
          eLinkContent: ''
        }
      ]
    });
  };

  const removeAudience = (index) => {
    const newAudiences = editedOrder.audiences.filter((_, i) => i !== index);
    setEditedOrder({
      ...editedOrder,
      audiences: newAudiences
    });
  };

  // Build a stable group key for an audience at a given flat index
  const getAudienceGroupKey = (audience, flatIndex) =>
    audience.adGroup != null ? String(audience.adGroup) : `solo-${flatIndex}`;

  // Update a group-level field (channel, adType, dates, eLink) across all schools in a group
  const handleGroupFieldChange = (adGroupKey, field, value) => {
    const newAudiences = editedOrder.audiences.map((audience, index) => {
      if (getAudienceGroupKey(audience, index) !== adGroupKey) return audience;
      const updated = { ...audience, [field]: value };
      if (field === 'channel') {
        const availableAdTypes = getAvailableAdTypes(value);
        if (updated.adType && !availableAdTypes.includes(updated.adType)) {
          updated.adType = '';
        }
      }
      return updated;
    });
    setEditedOrder({ ...editedOrder, audiences: newAudiences });
  };

  // Add a new school row to an existing group
  const addSchoolToGroup = (adGroupKey) => {
    const template = editedOrder.audiences.find((a, i) => getAudienceGroupKey(a, i) === adGroupKey);
    if (!template) return;
    setEditedOrder({
      ...editedOrder,
      audiences: [
        ...editedOrder.audiences,
        {
          organization: null,
          channel: template.channel,
          adType: template.adType,
          startDate: template.startDate,
          endDate: template.endDate,
          price: '',
          discount: '0',
          discountType: template.discountType || '$',
          eLinkType: template.eLinkType || 'None',
          eLinkContent: template.eLinkContent || '',
          adGroup: template.adGroup
        }
      ]
    });
  };

  const handleDateButtonClick = async (field) => {
    try {
      // First update the date field
      const dateResponse = await fetch(`${config.apiUrl}/api/orders/${order.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          [field]: new Date().toISOString()
        })
      });
      
      if (!dateResponse.ok) throw new Error('Failed to update order date');
      
      // Then update the status using the dedicated status endpoint
      const newStatus = getStatusForField(field);
      const statusResponse = await fetch(`${config.apiUrl}/api/orders/${order.id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          status: newStatus
        })
      });
      
      if (!statusResponse.ok) throw new Error('Failed to update order status');
      
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error updating order:', error);
    }
  };

  const getStatusForField = (field) => {
    switch (field) {
      case 'proofDate':
        return 'Proofing';
      case 'approvedDate':
        return 'Approved';
      case 'uploadDate':
        return 'Uploaded';
      case 'invoiceDate':
        return 'Invoiced';
      default:
        return order.status;
    }
  };

  const handleSave = async () => {
    try {
      // Prepare school order data for submission
      const schoolData = orderType === 'school' ? {
        schoolInfo: {
          name: editedOrder.businessName || order?.businessName || ''
        },
        submittedBy: {
          firstName: editedOrder.firstName || '',
          lastName: editedOrder.lastName || '',
          position: editedOrder.position || '',
          email: editedOrder.email || '',
          phone: editedOrder.phone || ''
        },
        newsletter: {
          numberOfFamilies: editedOrder.numberOfFamilies || '',
          issuePerTerm: editedOrder.issuesPerTerm || ''
        },
        paymentModel: editedOrder.paymentModel || 'adSupported',
        endDate: editedOrder.endDate || null,
        productsOrdered: editedOrder.productsOrdered || []
      } : null;
      
      // Include endDate in the submission for school orders
      if (orderType === 'school' && schoolData && editedOrder.endDate) {
        schoolData.endDate = editedOrder.endDate;
      }
      
      // Format productsOrdered as a string if it's an array
      if (schoolData?.productsOrdered && Array.isArray(schoolData.productsOrdered)) {
        schoolData.productsOrdered = schoolData.productsOrdered.length > 0 ? 
            schoolData.productsOrdered.join(', ') : 
            schoolData.productsOrdered;
      }
      
      
        
        if (!editedOrder || !editedOrder.id) {
        console.error('Cannot update order: Missing order ID', editedOrder);
        
        // Don't show error immediately - wait a short time to see if it resolves
        // This prevents showing errors for transient states
        setTimeout(() => {
          // Only show error if we're still in the dialog
          if (open) {
            setSnackbar({
              open: true,
              message: 'Error: Cannot update order without a valid ID',
              severity: 'error'
            });
          }
        }, 500);
        
        return;
      }
      
      // Track whether the operation succeeded
      let updateSucceeded = false;
      
      // Log status change for debugging reactivation logic
      if (editedOrder.status !== order?.status) {
        const expectedActive = !['cancelled', 'invoiced'].includes(editedOrder.status?.toLowerCase());
      }
      
        
        // Use explicit orderType from the original order object for proper handling
        if (order?.orderType === 'school' && schoolData) {
          try {
            // School order logic...
            const updatedOrder = await updateSchoolOrder(
              editedOrder.id,
              schoolData,
              editedOrder.businessName,
              editedOrder.businessNumber
            );
            
            updateSucceeded = true;
            onUpdate(updatedOrder);
            onClose();
            
            // Don't show success message if we've just closed the dialog
            setTimeout(() => {
              setSnackbar({
                open: true,
                message: 'School order updated successfully',
                severity: 'success'
              });
            }, 100);
          } catch (error) {
            console.error('Error updating school order:', error);
            
            // Ask user if they want to continue with client-side only changes
            const userConfirm = window.confirm(
              'Unable to save changes to the server. Would you like to continue with temporary local changes only? ' +
              'These changes will not be saved to the database.'
            );
            
            if (userConfirm) {
              // Continue with client-side only
              const localUpdate = {
                ...editedOrder,
                notes: JSON.stringify(schoolData),
                // Ensure we have all required fields for display
                title: `School Order - ${editedOrder.businessName}`,
                organizationId: parseInt(editedOrder.businessNumber) || null,
                // Remove status field for school orders
                status: undefined
              };
              
              onUpdate(localUpdate);
              onClose();
              setSnackbar({
                open: true,
                message: 'Warning: Changes are displayed locally only and were not saved to the database.',
                severity: 'warning'
              });
            } else {
              // User chose not to continue
              setSnackbar({
                open: true,
                message: 'Update cancelled.',
                severity: 'info'
              });
            }
          }
        } else {
          // Ad order, stationery order, and other order types
          const isAdOrder = order?.orderType === 'ad';
          const isStationeryOrder = order?.orderType === 'stationery' || order?.order_type === 'stationery';
          
          let endpoint;
          if (isAdOrder) {
            endpoint = `${config.apiUrl}/api/ad-orders/${editedOrder.id}`;
          } else if (isStationeryOrder) {
            endpoint = `${config.apiUrl}/api/stationery-orders/${editedOrder.id}`;
          } else {
            endpoint = `${config.apiUrl}/api/orders/${editedOrder.id}`;
          }
          

          
          // Prepare the payload...
          let payload = {};
          
          if (isAdOrder) {
            // For ad orders, only include fields that belong to the ad_orders table
            // and avoid sending fields that only exist in one table to the other
            
            // Format audience dates properly
            const formattedAudiences = Array.isArray(editedOrder.audiences) 
              ? editedOrder.audiences.map(audience => {
                  // Create a copy of the audience to avoid mutating the original
                  const formattedAudience = { ...audience };
                  
                  // Format startDate if it exists
                  if (formattedAudience.startDate) {
                    // Ensure we have just the date part (YYYY-MM-DD)
                    formattedAudience.startDate = typeof formattedAudience.startDate === 'string' 
                      ? formattedAudience.startDate.split('T')[0] 
                      : formattedAudience.startDate;
                  }
                  
                  // Format endDate if it exists
                  if (formattedAudience.endDate) {
                    // Ensure we have just the date part (YYYY-MM-DD)
                    formattedAudience.endDate = typeof formattedAudience.endDate === 'string' 
                      ? formattedAudience.endDate.split('T')[0] 
                      : formattedAudience.endDate;
                  }
                  
                  return {
                    ...formattedAudience,
                    organization: audience.organization_id || audience.organization || editedOrder.organization_id || null,
                    adType: audience.adType || audience.ad_type || '' // Explicitly include adType field
                  };
                }) 
              : [];
            
            payload = {
              // Always include the ID
              id: editedOrder.id,
              
              // Basic order fields
              orderType: 'ad',
              
              // Ad-specific fields
              organization: editedOrder.organization_id || editedOrder.organizationId,
              productType: editedOrder.productType || '',
              businessName: editedOrder.businessName || '',
              businessNumber: editedOrder.businessNumber || '',
              businessType: editedOrder.businessType || '',
              notes: typeof editedOrder.notes === 'string' ? editedOrder.notes : JSON.stringify(editedOrder.notes),
              designNotes: editedOrder.designNotes || '',
              customerNotes: editedOrder.customerNotes || '',
              status: editedOrder.status || 'prospect',

              // Invoice settings
              invoiceLater: editedOrder.invoiceLater || false,
              invoiceDate: editedOrder.invoiceDate || null,
              
              // Use the formatted audiences array
              audiences: formattedAudiences
            };
          } else if (isStationeryOrder) {
            // For stationery orders, include stationery-specific fields with correct field names
            payload = {
              // These fields use camelCase as expected by the server
              productType: editedOrder.productType || '',
              quantity: Math.max(1, parseInt(editedOrder.quantity) || 1), // Ensure quantity is at least 1
              size: editedOrder.size || '',
              stock: editedOrder.stock || '',
              finish: editedOrder.finish || '',
              setupFee: parseFloat(editedOrder.setupFee) || 0,
              price: parseFloat(editedOrder.price) || 0,
              margin: parseFloat(editedOrder.margin) || 0,
              freightPrice: parseFloat(editedOrder.freightPrice) || 0,
              gst: parseFloat(editedOrder.gst) || 0,
              totalPrice: parseFloat(editedOrder.totalPrice) || 0,
              artworkFee: parseFloat(editedOrder.artworkFee) || 0,
              additionalArtworkHours: parseFloat(editedOrder.additionalArtworkHours) || 0,
              discountType: editedOrder.discountType || '$',
              discountAmount: parseFloat(editedOrder.discountAmount) || 0,
              notes: typeof editedOrder.notes === 'string' ? editedOrder.notes : (editedOrder.notes ? JSON.stringify(editedOrder.notes) : ''),
              status: editedOrder.status || 'new'
            };
            
            // Only include organization_id if it's a valid integer
            const orgId = editedOrder.organizationId || editedOrder.organization_id || editedOrder.organization;
            if (orgId && !isNaN(parseInt(orgId))) {
              payload.organization_id = parseInt(orgId);
            }
            
            // Only include snake_case business fields if they have values
            if (editedOrder.businessName) {
              payload.business_name = editedOrder.businessName;
            }
            if (editedOrder.businessNumber) {
              payload.business_number = editedOrder.businessNumber;
            }
            if (editedOrder.businessType) {
              payload.business_type = editedOrder.businessType;
            }
          } else {
            // For other order types, include all fields
            payload = {
              ...editedOrder,
              notes: typeof editedOrder.notes === 'string' ? editedOrder.notes : JSON.stringify(editedOrder.notes)
            };
          }
          

          
          // Debug logging for payload
          
          const response = await axios.put(
            endpoint,
            payload,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`
              }
            }
          );
          
          updateSucceeded = true;
          onUpdate(response.data);
          onClose();
          
          // Only show success message if no other errors occurred
          setTimeout(() => {
            if (updateSucceeded) {
              setSnackbar({
                open: true,
                message: 'Order updated successfully',
                severity: 'success'
              });
            }
          }, 100);
        }
    } catch (error) {
      console.error('Error updating order:', error);
      
      // Log detailed error information for debugging
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
        
        // Show specific validation errors if available
        if (error.response.data && error.response.data.errors) {
          console.error('Validation errors:', error.response.data.errors);
          const errorMessages = error.response.data.errors.map(err => err.msg).join(', ');
          setSnackbar({
            open: true,
            message: `Validation errors: ${errorMessages}`,
            severity: 'error'
          });
        } else if (error.response.data && error.response.data.message) {
          setSnackbar({
            open: true,
            message: `Error: ${error.response.data.message}`,
            severity: 'error'
          });
        } else {
          setSnackbar({
            open: true,
            message: `Error updating order: ${error.message}`,
            severity: 'error'
          });
        }
      } else {
        setSnackbar({
          open: true,
          message: `Error updating order: ${error.message}`,
          severity: 'error'
        });
      }
    }
  };

  const handleCancel = () => {
    setEditedOrder(order || {});
    onClose();
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Notes dialog handlers
  const handleOpenNotesDialog = (entityType, entityId, title, existingNotes = []) => {
    setNotesDialog({
      open: true,
      title,
      entityType,
      entityId,
      notes: existingNotes
    });
  };

  const handleCloseNotesDialog = () => {
    setNotesDialog({
      open: false,
      title: '',
      entityType: '',
      entityId: '',
      notes: []
    });
  };

  const handleNotesUpdate = (updatedNotes, newNote) => {
    
    // Show success message
    setSnackbar({ 
      open: true, 
      message: 'Note added successfully!', 
      severity: 'success' 
    });
    
    // Optionally refresh order data
    if (onUpdate) {
      onUpdate(editedOrder);
    }
  };

  // Handle adding embedded notes (stationery order notes section)
  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    try {
      const userName = user?.name || user?.email || 'User';
      // Use the same "(date) User from Source: content" structured format used
      // everywhere else so this note parses and displays consistently via the
      // shared parseNotesBlob()/NoteCard system, instead of a raw "---" blob.
      const formattedNote = createStructuredNote(newNote.trim(), userName, 'stationeryOrder', 'general');

      // Update the order's notes in editedOrder to trigger re-render. This is
      // persisted the same way the rest of the order's fields are - via the
      // order's Save action - so it stays in sync with the backend record.
      const currentNotes = editedOrder.notes || '';
      const newNoteText = currentNotes ? `${currentNotes}\n\n${formattedNote}` : formattedNote;

      setEditedOrder(prev => ({
        ...prev,
        notes: newNoteText
      }));

      // Clear the input
      setNewNote('');

      // Show success message
      setSnackbar({ 
        open: true, 
        message: 'Note added successfully!', 
        severity: 'success' 
      });

    } catch (error) {
      console.error('Error adding note:', error);
      setSnackbar({ 
        open: true, 
        message: 'Error adding note. Please try again.', 
        severity: 'error' 
      });
    }
  };

  // Helper function to get total note count including localStorage
  const getTotalNoteCount = (entity, entityType) => {
    // Count database notes
    let dbNoteCount = 0;
    if (entityType === 'order') {
      if (entity.notes) dbNoteCount++;
      if (entity.designNotes) dbNoteCount++;
    }
    
    // Count localStorage notes
    const entityId = entity.id;
    const storageKey = `notes_${entityType}_${entityId}`;
    const storageNotes = JSON.parse(localStorage.getItem(storageKey) || '[]');
    
    return dbNoteCount + storageNotes.length;
  };

  const handleDeleteClick = async () => {
    // Add confirmation dialog
    const confirmDelete = window.confirm('Are you sure you want to archive this order? You can restore it from the Archive page later.');
    if (!confirmDelete) {
      return; // User cancelled the delete operation
    }
    
    try {
      // Save the current order type before deletion for proper tab restoration
      const orderType = order?.orderType;
      
      // Store current tab selection based on order type
      if (orderType === 'school') {
        localStorage.setItem('ordersActiveTab', '1'); // School orders tab
      } else if (orderType === 'ad') {
        localStorage.setItem('ordersActiveTab', '0'); // Advertiser orders tab
      } else if (orderType === 'stationery') {
        localStorage.setItem('ordersActiveTab', '2'); // Stationery orders tab
      }
      
      // Determine the correct endpoint based on order type
      let endpoint;
      
      // Proper order type identification for deletion
      if (orderType === 'school') {
        endpoint = `${config.apiUrl}/api/school-orders/${editedOrder.id}`;
      } else if (orderType === 'ad') {
        endpoint = `${config.apiUrl}/api/ad-orders/${editedOrder.id}`;
      } else {
        // Default fallback for other order types
        endpoint = `${config.apiUrl}/api/orders/${editedOrder.id}`;
      }
      
      // Make API call to archive the order (not delete) with enhanced error handling
      const response = await axios.put(`${config.apiUrl}/api/orders/${editedOrder.id}/archive`, {}, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      }).catch(error => {
        console.error('Delete request failed:', error.response?.status, error.response?.data);
        
        // Handle specific error cases
        if (error.response?.status === 403) {
          // If permission error, try with the general orders endpoint instead (archive)
          return axios.put(`${config.apiUrl}/api/orders/${editedOrder.id}/archive`, {}, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          });
        }
        throw error; // Re-throw if not a permission error or the fallback also failed
      });

      // Show success message before closing
      setSnackbar({
        open: true,
        message: 'Order archived successfully',
        severity: 'success'
      });
      
      // Call onUpdate and close after a brief delay to show the snackbar
      setTimeout(() => {
        onUpdate('deleted');
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error archiving order:', error);
      
      // Enhanced error message
      let errorMessage = error.message || 'Unknown error';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to delete this order. Please contact an administrator.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Order not found. It may have been deleted already.';
      }
      
      setSnackbar({
        open: true,
        message: `Error deleting order: ${errorMessage}`,
        severity: 'error'
      });
    }
  };

  // Process any audiences if present
  if (Array.isArray(editedOrder.audiences)) {
    editedOrder.audiences = editedOrder.audiences.map(audience => {
      // Normalize each audience field with both naming conventions
      return {
        ...audience,
        organization_id: audience.organization_id || audience.organizationId || '',
        organizationId: audience.organizationId || audience.organization_id || '',
        
        startDate: audience.startDate || audience.start_date || '',
        start_date: audience.startDate || audience.start_date || '',
        
        endDate: audience.endDate || audience.end_date || '',
        end_date: audience.endDate || audience.end_date || '',
        
        eLinkType: audience.eLinkType || audience.e_link_type || '',
        e_link_type: audience.eLinkType || audience.e_link_type || '',
        
        eLinkContent: audience.eLinkContent || audience.e_link_content || '',
        e_link_content: audience.eLinkContent || audience.e_link_content || '',
        
        // Ensure adType is preserved
        adType: audience.adType || audience.ad_type || '',
        ad_type: audience.adType || audience.ad_type || '',
        
        // Ensure addons are preserved (should already be included in spread, but explicit is better)
        addons: audience.addons || {}
      };
    });
  }

  // Helper function to determine product type when not explicitly set
  const determineProductType = (order) => {
    // If notes indicate order was created from offer or enquiry, it's a Lead
    const notes = order.notes || '';
    if (notes.toLowerCase().includes('order created from offer') || 
        notes.toLowerCase().includes('order from enquiry') ||
        notes.toLowerCase().includes('created from offer')) {
      return 'Lead';
    }
    
    // Check if this order has minimal data (typical of leads)
    const hasMinimalData = !order.startDate && !order.endDate && (!order.audiences || order.audiences.length === 0);
    if (hasMinimalData) {
      return 'Lead';
    }
    
    // Default fallback
    return '';
  };

  const mapOrderData = (order) => {
    // Check the order type
    let orderType = 'ad'; // Default to ad order
    if (order.orderType === 'school' || order.order_type === 'school') {
      orderType = 'school';
    } else if (order.orderType === 'stationery' || order.order_type === 'stationery') {
      orderType = 'stationery';
    } else if (order.orderType === 'update' || order.order_type === 'update') {
      orderType = 'update';
    }
    
    // Default values common to all order types - include ALL fields from fresh data
    const mappedData = {
      id: order.id,
      orderType: orderType,
      status: order.status || 'pending',
      active: order.active, // Include active status
      
      // Handle business-related fields, treating organization as business number
      businessNumber: order.organization || order.organization_id || order.businessNumber || order.business_number || order.id || '',
      businessName: order.businessName || order.business_name || '',
      businessType: order.businessType || order.business_type || '',
      
      // Organisation fields
      organization: order.organization || order.organization_id || null,
      organizationId: order.organizationId || order.organization_id || null,
      organizationName: order.organizationName || '',
      organizationData: order.organizationData || null,
      
      // Notes and design fields
      notes: order.notes || '',
      designNotes: order.designNotes || order.design_notes || '',
      
      // Timestamps
      createdAt: order.createdAt || '',
      updatedAt: order.updatedAt || '',
      
      // User info
      salesPersonId: order.salesPersonId || order.sales_person_id || '',
      salesPersonName: order.salesPersonName || order.sales_person_name || '',
      createdById: order.createdById || order.created_by_id || '',
      createdByName: order.createdByName || order.created_by_name || '',
      
      // Product and pricing
      productType: order.productType || order.product_type || determineProductType(order),
      totalPrice: order.totalPrice || order.total_price || '0.00',
      
      // Stationery-specific fields
      quantity: order.quantity || '',
      size: order.size || '',
      sides: order.sides || '',
      stock: order.stock || '',
      finish: order.finish || '',
      price: order.price || '0.00',
      freightPrice: order.freightPrice || order.freight_price || '0.00',
      additionalArtworkHours: order.additionalArtworkHours || order.additional_artwork_hours || '0',
      margin: order.margin || '0.00',
      artworkFee: order.artworkFee || order.artwork_fee || '0.00',
      setupFee: order.setupFee || order.setup_fee || '0.00',
      gst: order.gst || '0.00',
      discountType: order.discountType || order.discount_type || '$',
      discountAmount: order.discountAmount || order.discount_amount || '0.00',
      
      // Audiences
      audiences: order.audiences || [],

      // Links and certificates
      proofLink: order.proofLink || order.proof_link || null,
      invoiceLink: order.invoiceLink || (order.invoiceUrl ? `${config.apiUrl}/proof/invoice/${order.invoiceUrl}` : null),
      invoiceUrl: order.invoiceUrl || null,
      invoiceNumber: order.invoiceNumber || null,
      certToken: order.certToken || null,
      // Offer acceptance certificate (set when this order was created from an accepted offer)
      offerCertificateUrl: order.offerCertificateUrl || null,
      offerAcceptorName: order.offerAcceptorName || null,
      offerAcceptedAt: order.offerAcceptedAt || null,

      // School order fields
      schoolName: order.schoolName || '',
      contactPerson: order.contactPerson || '',
      contactName: order.contactName || '',
      firstName: order.firstName || '',
      lastName: order.lastName || '',
      position: order.position || '',
      email: order.email || '',
      phone: order.phone || '',
      numberOfFamilies: order.numberOfFamilies ?? '',
      issuesPerTerm: order.issuesPerTerm ?? '',
      paymentModel: order.paymentModel || '',
      productsOrdered: order.productsOrdered || '',
      endDate: order.endDate || null,
      active: order.active,

      // Paid status
      isPaid: order.isPaid ?? 0,

      // Reorder submission fields
      reorderAddress: order.reorderAddress || '',
      reorderPostalAddress: order.reorderPostalAddress || '',
      generalEmail: order.generalEmail || '',
      generalPhone: order.generalPhone || '',
      additionalAccount: order.additionalAccount ?? null,
      deploymentsPerTerm: order.deploymentsPerTerm ?? '',
      newsletterContact: order.newsletterContact || null,
      principalContact: order.principalContact || null,
      businessManagerContact: order.businessManagerContact || null,
      accountsContact: order.accountsContact || null,
      signatureName: order.signatureName || '',
      reorderSubmittedAt: order.reorderSubmittedAt || null,

      // Invoice settings for ad orders
      invoiceLater: order.invoiceLater ?? false,
      invoiceDate: order.invoiceDate || null,
      paid: order.paid ?? false,
    };

    return mappedData;
  };

  return (
    <div>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {(order?.title ? `"${order.title}" - ` : order?.id ? `${order.id} - ` : '') +
               (order?.orderType === 'school' || order?.order_type === 'school' ? 'School Order' : 
               order?.orderType === 'stationery' || order?.order_type === 'stationery' ? 'Stationery Order' : 
               order?.orderType === 'update' || order?.order_type === 'update' ? 'Update Order' :
               'Advertising Order')} Details
            </Typography>
            <Typography variant="subtitle2" color="textSecondary">
              Order ID: {editedOrder?.id || order?.id || 'Unknown'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {!order ? (
            <Box sx={{ textAlign: 'center', p: 3 }}>
              <Typography>Loading order details...</Typography>
            </Box>
          ) : order.orderType === 'update' || order?.order_type === 'update' ? (
            // Update Order Details - Show what changed
            <UpdateOrderChanges order={order} />
          ) : order.orderType === 'school' || order?.order_type === 'school' ? (
            // School Order Details
            (() => {
              const o = editedOrder;
              // Parse JSON contact fields from reorder submission
              const parseContact = (raw) => {
                if (!raw) return null;
                if (typeof raw === 'object') return raw;
                try { return JSON.parse(raw); } catch { return null; }
              };
              const newsletterContact = parseContact(o.newsletterContact);
              const principalContact  = parseContact(o.principalContact);
              const bmContact         = parseContact(o.businessManagerContact);
              const accountsContact   = parseContact(o.accountsContact);
              const baseUrl = config.apiUrl;
              const invoiceLink = o.invoiceUrl ? `${baseUrl}/proof/invoice/${o.invoiceUrl}` : null;
              const certLink    = o.certToken  ? `${baseUrl}/reorders/certificates/cert-${o.certToken}.html` : null;

              const FieldRow = ({ label, value }) => value ? (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">{label}</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>{value}</Typography>
                </Grid>
              ) : null;

              const ContactBlock = ({ title, contact }) => {
                if (!contact || (!contact.name && !contact.email && !contact.phone)) return null;
                return (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 700, mb: 0.5 }}>{title}</Typography>
                    <Grid container spacing={1}>
                      <FieldRow label="Name"          value={contact.name} />
                      <FieldRow label="Phone"         value={contact.phone} />
                      <FieldRow label="Email"         value={contact.email} />
                      <FieldRow label="General Email" value={contact.generalEmail} />
                    </Grid>
                  </Box>
                );
              };

              return (
                <Box>
                  {/* Documents */}
                  {(invoiceLink || certLink) && (
                    <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="h6" gutterBottom>Documents</Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {invoiceLink && (
                          <Button variant="outlined" color="primary" size="small"
                            onClick={() => window.open(invoiceLink, '_blank')}>
                            View Invoice {o.invoiceNumber ? `(${o.invoiceNumber})` : ''}
                          </Button>
                        )}
                        {certLink && (
                          <Button variant="outlined" color="secondary" size="small"
                            onClick={() => window.open(certLink, '_blank')}>
                            View Order Certificate
                          </Button>
                        )}
                      </Box>
                    </Paper>
                  )}

                  {/* Order Summary */}
                  <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      <SchoolIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Order Summary
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={2}>
                      <FieldRow label="School Name" value={o.schoolName} />
                      <FieldRow label="Status" value={o.status} />
                      <FieldRow label="Payment Model" value={o.paymentModel} />
                      <FieldRow label="Number of Families" value={o.numberOfFamilies} />
                      <FieldRow label="Deployments per Term" value={o.deploymentsPerTerm || o.issuesPerTerm} />
                      <FieldRow label="Additional Account" value={o.additionalAccount == null ? null : (o.additionalAccount ? 'Yes' : 'No')} />
                      <FieldRow label="End Date" value={o.endDate ? formatDate(o.endDate) : null} />
                      <FieldRow label="Total (inc GST)" value={o.totalPrice != null ? `$${parseFloat(o.totalPrice).toFixed(2)}` : null} />
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Paid</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <Chip
                            label={o.isPaid ? 'Paid' : 'Not Paid'}
                            color={o.isPaid ? 'success' : 'default'}
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            color={o.isPaid ? 'warning' : 'success'}
                            sx={{ fontSize: '0.7rem', py: 0.25, px: 1 }}
                            onClick={async () => {
                              try {
                                const token = localStorage.getItem('token');
                                const newVal = o.isPaid ? 0 : 1;
                                await fetch(`${config.apiUrl}/api/school-orders/${o.id}/paid`, {
                                  method: 'PATCH',
                                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                  body: JSON.stringify({ isPaid: newVal })
                                });
                                setEditedOrder(prev => ({ ...prev, isPaid: newVal }));
                              } catch (err) {
                                console.error('Failed to update isPaid:', err);
                              }
                            }}
                          >
                            Mark as {o.isPaid ? 'Not Paid' : 'Paid'}
                          </Button>
                        </Box>
                      </Grid>
                      {o.productsOrdered && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" color="text.secondary">Products</Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                            {o.productsOrdered.split(',').map((p, i) => (
                              <Chip key={i} label={p.trim()} size="small" variant="outlined"
                                color={p.trim() === 'SchoolLink' ? 'success' : p.trim() === 'ePublisher' ? 'primary' : 'warning'} />
                            ))}
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>

                  {/* Address */}
                  <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="h6" gutterBottom>Address</Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={2}>
                      <FieldRow label="Address"        value={o.reorderAddress} />
                      <FieldRow label="Postal Address" value={o.reorderPostalAddress} />
                    </Grid>
                  </Paper>

                  {/* Contacts */}
                  <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      <FamilyRestroomIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Contacts
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <ContactBlock title="Newsletter Contact" contact={newsletterContact || { name: o.contactPerson || o.contactName, email: o.email, phone: o.phone }} />
                    <ContactBlock title="Principal"         contact={principalContact} />
                    <ContactBlock title="Business Manager"  contact={bmContact} />
                    <ContactBlock title="Accounts"          contact={accountsContact} />
                    {(o.generalEmail || o.generalPhone) && (
                      <Box>
                        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 700, mb: 0.5 }}>General Contact</Typography>
                        <Grid container spacing={1}>
                          <FieldRow label="General Email" value={o.generalEmail} />
                          <FieldRow label="General Phone" value={o.generalPhone} />
                        </Grid>
                      </Box>
                    )}
                  </Paper>

                  {/* Digital Signature */}
                  {o.signatureName && (
                    <Paper sx={{ p: 2, mb: 2, borderLeft: '4px solid', borderColor: 'primary.main', bgcolor: 'grey.50' }}>
                      <Typography variant="h6" gutterBottom>Digital Signature</Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Typography variant="body2" color="text.secondary" gutterBottom>Signed by</Typography>
                      <Typography variant="h6" sx={{ fontStyle: 'italic', mb: 1 }}>{o.signatureName}</Typography>
                      {o.reorderSubmittedAt && (
                        <Typography variant="caption" color="text.secondary">
                          Submitted on {formatDate(o.reorderSubmittedAt)} � signee confirmed authorisation and agreed to Austnews Terms & Conditions and Privacy Statement on behalf of {o.schoolName}.
                        </Typography>
                      )}
                    </Paper>
                  )}

                  {/* Notes */}
                  <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="h6" gutterBottom>Order Notes</Typography>
                    <Divider sx={{ mb: 2 }} />
                    <EmbeddedNotes order={editedOrder} orderType="school" />
                  </Paper>
                </Box>
              );
            })()
          ) : order?.orderType === 'ad' || order?.order_type === 'ad' ? (
            // Advertising Order Details Form
            <Box>
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  <AttachMoneyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Advertiser Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  {/* TOP SECTION */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle1" fontWeight="bold">ID:</Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {editedOrder.businessNumber || editedOrder.id || order?.id || '�'}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle1" fontWeight="bold">Organisation:</Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {editedOrder.organizationData?.name || 
                       editedOrder.organizationName || 
                       organizations.find(org => 
                         org.id === (editedOrder.organization || editedOrder.organizationId))?.name || 'N/A'}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle1" fontWeight="bold">Created By:</Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {editedOrder.createdByName || 'Unknown'}
                    </Typography>
                  </Grid>
                  
                  {/* BOTTOM SECTION */}
                  {/* Only show sales person field for ad orders, not school orders */}
                  {orderType !== 'school' && (
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth margin="normal">
                        <InputLabel>Sales Person</InputLabel>
                        <Select
                          value={editedOrder.salesPersonId || ''}
                          onChange={handleChange('salesPersonId')}
                          label="Sales Person"
                        >
                          <MenuItem value="">
                            <em>None</em>
                          </MenuItem>
                          {Array.isArray(salesPeople) ? salesPeople.map((person) => (
                            <MenuItem key={person.id} value={person.id}>
                              {person.name}
                            </MenuItem>
                          )) : (
                            <MenuItem disabled>No sales people available</MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                  
                  {/* Status dropdown hidden - managed via workflow actions only */}
                  
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ mt: 3, mb: 1 }}>
                      <Typography variant="body1" component="div" sx={{ mb: 1 }}>
                        Active Status
                      </Typography>
                      <Chip 
                        label={editedOrder.active === false || editedOrder.active === 0 ? "Inactive" : "Active"}
                        color={editedOrder.active === false || editedOrder.active === 0 ? "error" : "success"}
                        sx={{ 
                          fontWeight: 'medium',
                          minWidth: '80px'
                        }}
                      />
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ mt: 3, mb: 1 }}>
                      <Typography variant="body1" component="div" sx={{ mb: 1 }}>
                        Payment Status
                      </Typography>
                      <Chip 
                        label={editedOrder.paid ? "Paid" : "Not Paid"}
                        color={editedOrder.paid ? "success" : "warning"}
                        sx={{ 
                          fontWeight: 'medium',
                          minWidth: '80px'
                        }}
                      />
                    </Box>
                  </Grid>
                  
                  {/* Active Links Section */}
                  {(() => {
                    const proofAccepted = ['approved', 'invoiced', 'paid', 'uploaded'].includes(
                      (editedOrder.status || '').toLowerCase()
                    );
                    const hasLinks = editedOrder.proofLink || editedOrder.invoiceLink || proofAccepted || editedOrder.offerCertificateUrl;
                    if (!hasLinks) return null;
                    return (
                      <Grid item xs={12}>
                        <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Active Links & Certificates
                          </Typography>
                          {/* Proof link � only show while proof is still active (hide once accepted) */}
                          {editedOrder.proofLink && !proofAccepted && (
                            <Box sx={{ mb: 1 }}>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => window.open(editedOrder.proofLink, '_blank')}
                                sx={{ mr: 1 }}
                              >
                                View Proof
                              </Button>
                              <Typography variant="caption" color="text.secondary">
                                {editedOrder.proofLink}
                              </Typography>
                            </Box>
                          )}
                          {/* Proof Certificate � show once proof has been accepted */}
                          {proofAccepted && (
                            <Box sx={{ mb: 1, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => window.open(`${config.apiUrl}/api/proof/accepted/${editedOrder.id}`, '_blank')}
                              >
                                View Proof Certificate
                              </Button>
                              <Button
                                variant="contained"
                                size="small"
                                color="success"
                                onClick={() => window.open(`${config.apiUrl}/api/proof/accepted/${editedOrder.id}?format=pdf`, '_blank')}
                              >
                                Download PDF
                              </Button>
                              <Typography variant="caption" color="text.secondary">
                                Proof accepted by client with digital signature
                              </Typography>
                            </Box>
                          )}
                          {editedOrder.invoiceLink && (
                            <Box sx={{ mb: 1, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => window.open(editedOrder.invoiceLink, '_blank')}
                              >
                                View Invoice
                              </Button>
                              <Button
                                variant="contained"
                                size="small"
                                color="success"
                                onClick={() => window.open(`${config.apiUrl}/api/ad-orders/download/invoice/${editedOrder.id}`, '_blank')}
                              >
                                Download PDF
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                color="error"
                                onClick={async () => {
                                  if (!window.confirm('Are you sure you want to cancel this invoice? This will deactivate the invoice link.')) return;
                                  try {
                                    const token = await getAccessTokenSilently();
                                    const response = await axios.post(
                                      `${config.apiUrl}/api/proof/cancel-invoice/${editedOrder.id}`,
                                      { reason: 'Invoice cancelled by admin' },
                                      { headers: { Authorization: `Bearer ${token}` } }
                                    );
                                    if (response.data?.success) {
                                      if (onUpdate) onUpdate();
                                    }
                                  } catch (err) {
                                    console.error('Error cancelling invoice:', err);
                                  }
                                }}
                              >
                                Cancel Invoice
                              </Button>
                            </Box>
                          )}
                          {/* Offer Acceptance Certificate — shown when this order was created from an accepted offer */}
                          {editedOrder.offerCertificateUrl && (
                            <Box sx={{ mb: 1, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => window.open(editedOrder.offerCertificateUrl, '_blank')}
                              >
                                View Acceptance Certificate
                              </Button>
                              <Typography variant="caption" color="text.secondary">
                                Offer accepted by {editedOrder.offerAcceptorName || 'client'}
                                {editedOrder.offerAcceptedAt ? ` on ${new Date(editedOrder.offerAcceptedAt).toLocaleDateString('en-AU')}` : ''}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Grid>
                    );
                  })()}
                  
                  {/* Proof Information Section */}
                  {(editedOrder.status === 'Proof Pending' || editedOrder.status === 'proof_pending') && (
                    <Grid item xs={12}>
                      <ProofSection orderId={editedOrder.id} onProofCancelled={onUpdate} />
                    </Grid>
                  )}
                  
                  {/* Invoice links are shown in Active Links & Certificates above */}
                </Grid>
              </Paper>

              <Paper sx={{ p: 2, mb: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    Groups
                  </Typography>
                  <Button 
                    startIcon={<AddIcon />} 
                    onClick={addAudience} 
                    variant="outlined" 
                    color="primary"
                    size="small"
                  >
                    Add Group
                  </Button>
                </Box>
                <Divider sx={{ mb: 2 }} />
                
                {editedOrder.audiences && editedOrder.audiences.length > 0 ? (
                  (() => {
                    // Group audiences by adGroup so multi-school groups display correctly
                    const groupMap = new Map();
                    editedOrder.audiences.forEach((audience, index) => {
                      const key = getAudienceGroupKey(audience, index);
                      if (!groupMap.has(key)) {
                        groupMap.set(key, { groupKey: key, adGroup: audience.adGroup, schools: [] });
                      }
                      groupMap.get(key).schools.push({ ...audience, flatIndex: index });
                    });

                    return Array.from(groupMap.values()).map((group, groupIndex) => {
                      const rep = group.schools[0];
                      return (
                        <Box key={group.groupKey} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                          {/* Group header */}
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              Group {groupIndex + 1}
                            </Typography>
                            <Button
                              startIcon={<AddIcon />}
                              onClick={() => addSchoolToGroup(group.groupKey)}
                              size="small"
                              variant="outlined"
                            >
                              Add School
                            </Button>
                          </Box>

                          {/* Group-level fields (shared across all schools in this group) */}
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <FormControl fullWidth margin="normal">
                                <InputLabel>Channel</InputLabel>
                                <Select
                                  value={rep.channel || ''}
                                  onChange={(e) => handleGroupFieldChange(group.groupKey, 'channel', e.target.value)}
                                  label="Channel"
                                >
                                  <MenuItem value="SchoolLink">SchoolLink</MenuItem>
                                  <MenuItem value="ePublisher">ePublisher</MenuItem>
                                  <MenuItem value="Paper">Paper</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <FormControl fullWidth margin="normal">
                                <InputLabel>Ad Type</InputLabel>
                                <Select
                                  value={rep.adType || ''}
                                  onChange={(e) => handleGroupFieldChange(group.groupKey, 'adType', e.target.value)}
                                  label="Ad Type"
                                  disabled={!rep.channel}
                                >
                                  {rep.channel ? (
                                    getAvailableAdTypes(rep.channel).map(adType => (
                                      <MenuItem key={adType} value={adType}>{adType}</MenuItem>
                                    ))
                                  ) : (
                                    <MenuItem value="" disabled>Select a channel first</MenuItem>
                                  )}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                label="Start Date"
                                value={rep.startDate ? rep.startDate.split('T')[0] : ''}
                                onChange={(e) => handleGroupFieldChange(group.groupKey, 'startDate', e.target.value)}
                                fullWidth
                                margin="normal"
                                type="date"
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <DurationSelector
                                startDate={rep.startDate ? rep.startDate.split('T')[0] : ''}
                                endDate={rep.endDate ? rep.endDate.split('T')[0] : ''}
                                onEndDateChange={(value) => handleGroupFieldChange(group.groupKey, 'endDate', value)}
                                label="End Date"
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <FormControl fullWidth margin="normal">
                                <InputLabel>eLink Type</InputLabel>
                                <Select
                                  value={rep.eLinkType || 'None'}
                                  onChange={(e) => handleGroupFieldChange(group.groupKey, 'eLinkType', e.target.value)}
                                  label="eLink Type"
                                >
                                  <MenuItem value="Website">Website</MenuItem>
                                  <MenuItem value="Email">Email</MenuItem>
                                  <MenuItem value="None">None</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            {rep.eLinkType && rep.eLinkType !== 'None' && (
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  label="eLink Content"
                                  value={rep.eLinkContent || ''}
                                  onChange={(e) => handleGroupFieldChange(group.groupKey, 'eLinkContent', e.target.value)}
                                  fullWidth
                                  margin="normal"
                                  required={rep.eLinkType !== 'None'}
                                  helperText={rep.eLinkType === 'Website' ? "Enter the full URL including https://" :
                                              rep.eLinkType === 'Email' ? "Enter the email address" : ""}
                                />
                              </Grid>
                            )}
                          </Grid>

                          {/* Schools in this group */}
                          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color: 'text.secondary' }}>
                            Schools ({group.schools.length})
                          </Typography>
                          {group.schools.map((school) => (
                            <Box key={school.flatIndex} sx={{ mb: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid #eee' }}>
                              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography variant="body2" fontWeight="bold">
                                  {school.schoolName || school.organizationName || 'Select School'}
                                </Typography>
                                <Button
                                  startIcon={<DeleteIcon />}
                                  onClick={() => removeAudience(school.flatIndex)}
                                  color="error"
                                  size="small"
                                >
                                  Remove
                                </Button>
                              </Box>
                              <Grid container spacing={2}>
                                {/* Organisation dropdown hidden - school name shown in header above */}
                                <Grid item xs={12} sm={6}>
                                  <TextField
                                    label="Price"
                                    value={school.price ? parseFloat(school.price).toFixed(2) : ''}
                                    onChange={(e) => handleAudienceChange(school.flatIndex, 'price', e.target.value)}
                                    fullWidth
                                    margin="normal"
                                    type="number"
                                    InputProps={{
                                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                    }}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <TextField
                                      label="Discount"
                                      value={school.discount || ''}
                                      onChange={(e) => handleAudienceChange(school.flatIndex, 'discount', e.target.value)}
                                      fullWidth
                                      margin="normal"
                                      type="number"
                                      InputProps={{
                                        startAdornment: (school.discountType || '$') === '$' ? <InputAdornment position="start">$</InputAdornment> : null,
                                        endAdornment: (school.discountType || '$') === '%' ? <InputAdornment position="end">%</InputAdornment> : null,
                                      }}
                                      sx={{ flex: 1 }}
                                    />
                                    <FormControl sx={{ minWidth: 80, marginTop: 2 }}>
                                      <InputLabel>Type</InputLabel>
                                      <Select
                                        value={school.discountType || '$'}
                                        onChange={(e) => handleAudienceChange(school.flatIndex, 'discountType', e.target.value)}
                                        label="Type"
                                        size="small"
                                      >
                                        <MenuItem value="$">$</MenuItem>
                                        <MenuItem value="%">%</MenuItem>
                                      </Select>
                                    </FormControl>
                                  </Box>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <TextField
                                    label="Sub Total"
                                    value={calculateSubTotal(school)}
                                    fullWidth
                                    margin="normal"
                                    InputProps={{
                                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                      readOnly: true,
                                    }}
                                  />
                                </Grid>
                                {school.addons && (school.addons.topSpot || school.addons.gif) && (
                                  <Grid item xs={12}>
                                    <Box sx={{ mt: 1, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                                      <Typography variant="subtitle2" gutterBottom>
                                        Add-ons Selected:
                                      </Typography>
                                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        {school.addons.topSpot && (
                                          <Chip label="Top Spot" color="primary" size="small" variant="outlined" />
                                        )}
                                        {school.addons.gif && (
                                          <Chip label="GIF Animation" color="secondary" size="small" variant="outlined" />
                                        )}
                                      </Box>
                                    </Box>
                                  </Grid>
                                )}
                              </Grid>
                            </Box>
                          ))}
                        </Box>
                      );
                    });
                  })()
                ) : (
                  <Typography color="textSecondary" align="center">No audiences added yet.</Typography>
                )}
              </Paper>

              {/* Total field for all audiences */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Total"
                    value={calculateTotal()}
                    fullWidth
                    margin="normal"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      readOnly: true,
                    }}
                  />
                </Grid>
              </Grid>

              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Additional Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Product Type</InputLabel>
                      <Select
                        value={editedOrder.productType || ''}
                        onChange={handleChange('productType')}
                        label="Product Type"
                      >
                        <MenuItem value="Repeat">Repeat</MenuItem>
                        <MenuItem value="New">New</MenuItem>
                        <MenuItem value="Lead">Lead</MenuItem>
                        <MenuItem value="Update">Update</MenuItem>
                        <MenuItem value="New to School">New to School</MenuItem>
                        <MenuItem value="Reactivated">Reactivated</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  {/* Invoice Settings */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Invoice Settings
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={editedOrder.invoiceLater || false}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setEditedOrder({
                                ...editedOrder,
                                invoiceLater: checked,
                                invoiceDate: checked ? editedOrder.invoiceDate : null
                              });
                            }}
                            color="primary"
                          />
                        }
                        label="Invoice Later"
                      />
                    </Box>
                  </Grid>
                  {!!editedOrder.invoiceLater && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="date"
                        label="Invoice Date"
                        value={editedOrder.invoiceDate ? editedOrder.invoiceDate.split('T')[0] : ''}
                        onChange={(e) => setEditedOrder({ ...editedOrder, invoiceDate: e.target.value })}
                        margin="normal"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        helperText="Select the date when this order should be invoiced"
                      />
                    </Grid>
                  )}
                  
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, mt: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        Order Notes
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <EmbeddedNotes order={editedOrder} orderType="order" />
                    </Paper>
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          ) : (
            // Stationery Orders (or fallback for unknown types)
            <Box>
              {/* Business Information */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Business Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle1" fontWeight="bold">ID:</Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {editedOrder.businessNumber || editedOrder.id || order?.id || '�'}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle1" fontWeight="bold">Organisation:</Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {editedOrder.organizationData?.name || 
                       editedOrder.organizationName || 
                       organizations.find(org => 
                         org.id === (editedOrder.organization || editedOrder.organizationId))?.name || 'N/A'}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle1" fontWeight="bold">Created By:</Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {editedOrder.createdByName || 'Unknown'}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Sales Person</InputLabel>
                      <Select
                        value={editedOrder.salesPersonId || ''}
                        onChange={handleChange('salesPersonId')}
                        label="Sales Person"
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {Array.isArray(salesPeople) ? salesPeople.map((person) => (
                          <MenuItem key={person.id} value={person.id}>
                            {person.name}
                          </MenuItem>
                        )) : (
                          <MenuItem disabled>No sales people available</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={(editedOrder.status || 'new').toLowerCase()}
                        onChange={handleChange('status')}
                        label="Status"
                      >
                        <MenuItem value="new">New</MenuItem>
                        <MenuItem value="quote">Quote</MenuItem>
                        <MenuItem value="quote active">Quote Active</MenuItem>
                        <MenuItem value="quote needs changes">Quote Needs Changes</MenuItem>
                        <MenuItem value="quote declined">Quote Declined</MenuItem>
                        <MenuItem value="quote accepted">Quote Accepted</MenuItem>
                        <MenuItem value="in design">In Design</MenuItem>
                        <MenuItem value="proof">Proof</MenuItem>
                        <MenuItem value="proof active">Proof Active</MenuItem>
                        <MenuItem value="proof needs changes">Proof Needs Changes</MenuItem>
                        <MenuItem value="proof accepted">Proof Accepted</MenuItem>
                        <MenuItem value="in production">In Production</MenuItem>
                        <MenuItem value="invoiced">Invoiced</MenuItem>
                        <MenuItem value="cancelled">Cancelled</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ mt: 3, mb: 1 }}>
                      <Typography variant="body1" component="div" sx={{ mb: 1 }}>
                        Active Status
                      </Typography>
                      <Chip 
                        label={editedOrder.active === false || editedOrder.active === 0 ? "Inactive" : "Active"}
                        color={editedOrder.active === false || editedOrder.active === 0 ? "error" : "success"}
                        sx={{ 
                          fontWeight: 'medium',
                          minWidth: '80px'
                        }}
                      />
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ mt: 3, mb: 1 }}>
                      <Typography variant="body1" component="div" sx={{ mb: 1 }}>
                        Payment Status
                      </Typography>
                      <Chip 
                        label={editedOrder.paid ? "Paid" : "Not Paid"}
                        color={editedOrder.paid ? "success" : "warning"}
                        sx={{ 
                          fontWeight: 'medium',
                          minWidth: '80px'
                        }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              {/* Product Information */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Product Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Product Type</InputLabel>
                      <Select
                        value={editedOrder.productType || ''}
                        onChange={handleChange('productType')}
                        label="Product Type"
                      >
                        <MenuItem value="">
                          <em>Select product type</em>
                        </MenuItem>
                        <MenuItem value="Business Cards">Business Cards</MenuItem>
                        <MenuItem value="Flyers">Flyers</MenuItem>
                        <MenuItem value="Brochures">Brochures</MenuItem>
                        <MenuItem value="Posters">Posters</MenuItem>
                        <MenuItem value="Banners">Banners</MenuItem>
                        <MenuItem value="Letterheads">Letterheads</MenuItem>
                        <MenuItem value="Envelopes">Envelopes</MenuItem>
                        <MenuItem value="NCR Forms">NCR Forms</MenuItem>
                        <MenuItem value="Booklets">Booklets</MenuItem>
                        <MenuItem value="Presentation Folders">Presentation Folders</MenuItem>
                        <MenuItem value="Stickers">Stickers</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      margin="normal"
                      type="number"
                      label="Quantity"
                      value={editedOrder.quantity || ''}
                      onChange={handleChange('quantity')}
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      margin="normal"
                      label="Size"
                      value={editedOrder.size || ''}
                      onChange={handleChange('size')}
                      placeholder="e.g., A4, DL, 90x55mm"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Sides</InputLabel>
                      <Select
                        value={editedOrder.sides || ''}
                        onChange={handleChange('sides')}
                        label="Sides"
                      >
                        <MenuItem value="">
                          <em>Select sides</em>
                        </MenuItem>
                        <MenuItem value="1">Single Sided</MenuItem>
                        <MenuItem value="2">Double Sided</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      margin="normal"
                      label="Stock"
                      value={editedOrder.stock || ''}
                      onChange={handleChange('stock')}
                      placeholder="e.g., 300gsm"
                    />
                  </Grid>
                  <Grid item xs={12} md={12}>
                    <TextField
                      fullWidth
                      margin="normal"
                      label="Finish"
                      value={editedOrder.finish || ''}
                      onChange={handleChange('finish')}
                      placeholder="e.g., Matt, Gloss, Uncoated"
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Pricing Information */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Pricing Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      margin="normal"
                      type="number"
                      label="Price"
                      value={editedOrder.price || ''}
                      onChange={handleChange('price')}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        inputProps: { min: 0, step: '0.01' }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      margin="normal"
                      type="number"
                      label="Freight Price"
                      value={editedOrder.freightPrice || ''}
                      onChange={handleChange('freightPrice')}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        inputProps: { min: 0, step: '0.01' }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      margin="normal"
                      type="number"
                      label="Additional Artwork Hours"
                      value={editedOrder.additionalArtworkHours || ''}
                      onChange={handleChange('additionalArtworkHours')}
                      inputProps={{ min: 0, step: '0.5' }}
                      helperText="Charged at $110/hour"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      margin="normal"
                      type="number"
                      label="Artwork Fee"
                      value={editedOrder.artworkFee || ''}
                      onChange={handleChange('artworkFee')}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        inputProps: { min: 0, step: '0.01' }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      margin="normal"
                      type="number"
                      label="Margin"
                      value={editedOrder.margin || ''}
                      onChange={handleChange('margin')}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        inputProps: { min: 0, step: '0.01' }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      margin="normal"
                      type="number"
                      label="Setup Fee"
                      value={editedOrder.setupFee || ''}
                      onChange={handleChange('setupFee')}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        inputProps: { min: 0, step: '0.01' }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      margin="normal"
                      type="number"
                      label="GST"
                      value={editedOrder.gst || ''}
                      onChange={handleChange('gst')}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        inputProps: { min: 0, step: '0.01' }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Discount Type</InputLabel>
                      <Select
                        value={editedOrder.discountType || '$'}
                        onChange={handleChange('discountType')}
                        label="Discount Type"
                      >
                        <MenuItem value="$">Dollar Amount ($)</MenuItem>
                        <MenuItem value="%">Percentage (%)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      margin="normal"
                      type="number"
                      label="Discount Amount"
                      value={editedOrder.discountAmount || ''}
                      onChange={handleChange('discountAmount')}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">
                          {editedOrder.discountType === '%' ? '%' : '$'}
                        </InputAdornment>,
                        inputProps: { min: 0, step: editedOrder.discountType === '%' ? '1' : '0.01' }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      margin="normal"
                      type="number"
                      label="Total Price"
                      value={editedOrder.totalPrice || ''}
                      onChange={handleChange('totalPrice')}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        inputProps: { min: 0, step: '0.01' }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'primary.light',
                          '& fieldset': {
                            borderColor: 'primary.main',
                            borderWidth: 2
                          }
                        },
                        '& .MuiInputLabel-root': {
                          fontWeight: 'bold'
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Order Notes */}
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Order Notes
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                {/* Add new note section */}
                <Box sx={{ mb: 3 }}>
                  <TextField
                    fullWidth
                    label="Add Note"
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    multiline
                    rows={3}
                    placeholder="Enter a note for this stationery order..."
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && e.ctrlKey) {
                        handleAddNote();
                      }
                    }}
                  />
                  <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleAddNote}
                      disabled={!newNote.trim()}
                      size="small"
                    >
                      Add Note
                    </Button>
                  </Box>
                </Box>

                {/* Existing notes display */}
                <EmbeddedNotes order={editedOrder} orderType="stationery" />
              </Paper>
            </Box>
          )}

          {/* Order Attachments Section - Available for ad and stationery orders only */}
          {/* Update orders handle attachments separately in UpdateOrderChanges component.
              Note: orderType state is set to 'advertising' (not 'ad') for ad orders -
              see setOrderType() above - so both values must be checked here. */}
          {order && (orderType === 'ad' || orderType === 'advertising' || orderType === 'stationery') && (
            <OrderAttachments 
              orderId={editedOrder.id}
              mode="full"
              title="Order Attachments"
            />
          )}

          {/* Order History Section - Available for ad orders */}
          {order && (orderType === 'ad' || orderType === 'advertising') && (
            <Box sx={{ mt: 2 }}>
              <WorkflowHistory orderId={editedOrder.id} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="primary">
            Cancel
          </Button>
          {order?.orderType === 'school' || order?.order_type === 'school' ? (
            // For school orders, only show the Delete button
            <Button 
              onClick={handleDeleteClick} 
              color="error" 
              startIcon={<DeleteIcon />}
            >
              Delete
            </Button>
          ) : (
            // For other order types, show both Save and Delete buttons
            <>
              <Button 
                onClick={handleSave} 
                color="primary" 
                variant="contained"
              >
                Save
              </Button>
              <Button 
                onClick={handleDeleteClick} 
                color="error" 
                startIcon={<DeleteIcon />}
              >
                Delete
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Notes Dialog */}
      <NotesDialog
        open={notesDialog.open}
        onClose={handleCloseNotesDialog}
        title={notesDialog.title}
        entityType={notesDialog.entityType}
        entityId={notesDialog.entityId}
        initialNotes={notesDialog.notes}
        onNotesUpdate={handleNotesUpdate}
      />

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default OrderDetail;
export { ProofButtons, PDFDownloadButton }; 